import { computed, effect, inject, Injectable, signal } from "@angular/core";
import { Router } from "@angular/router";
import { DateTime } from "luxon";
import { UtilsService } from "src/app/shared/services/utils.service";
import {
  AlertDefinition,
  AlertDefinitionData,
  AlertRule,
  maxValue,
  minValue,
  RuleType,
  SYSTEM_ALERT_DEFINITIONS,
} from "../models/alert-definition.model";
import {
  Alert,
  AlertData,
  AlertType,
  DeviceType,
  maxDistance,
} from "../models/alert.model";
import {
  Meter,
  METER_3P_TYPES,
  METER_DRAC_TYPES,
  MeterFeature,
} from "../models/meter.model";
import { OrderByDirection, OrderByItem } from "../models/search-result.model";
import { AlertDefinitionService } from "../services/alert-definition.service";
import { AlertService } from "../services/alert.service";
import { AlertTourScenario, AppStore } from "./app.store";
import { GatewayStore } from "./gateway.store";
import { MeterStore } from "./meter.store";
import { PlacesStore } from "./places.store";
import { ShowerheadStore } from "./showerhead.store";
import { UserStore } from "./user.store";

enum AlertScreenMode {
  Normal = "normal",
  Loading = "loading",
}

const LOG_TAG = "[ALERT-STORE] ";

@Injectable({
  providedIn: "root",
})
export class AlertStore {
  protected userStore: UserStore = inject(UserStore);
  protected appStore: AppStore = inject(AppStore);
  protected placesStore: PlacesStore = inject(PlacesStore);
  protected gatewayStore: GatewayStore = inject(GatewayStore);
  protected meterStore: MeterStore = inject(MeterStore);
  protected showerheadStore: ShowerheadStore = inject(ShowerheadStore);
  private utilsService: UtilsService = inject(UtilsService);
  private alertService: AlertService = inject(AlertService);
  private alertDefinitionService: AlertDefinitionService = inject(
    AlertDefinitionService
  );
  private router: Router = inject(Router);

  private screenMode: AlertScreenMode = AlertScreenMode.Normal;

  // Define signals for the store state
  private readonly forceRefresh = signal<boolean>(false);
  private readonly error = signal<string | null>(null);
  readonly error$ = this.error.asReadonly();

  // ---------------------------
  // --- touring
  // ---------------------------

  readonly touring = {
    welcomeStep: {
      url: "alerts",
      title: $localize`:@@alert.touring.welcomeTitle:Welcome to alerts section`,
      text: $localize`:@@alert.touring.welcomeText:You will learn how to manage your alerts and track your triggered alerts. Let's start with global view of all configured alerts.`,
    },
    ruleFilterStep: {
      url: "alerts",
      title: $localize`:@@alert.touring.yourRulesTitle:Your alerts`,
      text: $localize`:@@alert.touring.yourRulesText:Select the alert(s) whose triggers you want to display. Settings are only visible if a single alert is selected.`,
    },
    allTriggersStep: {
      url: "alerts",
      title: $localize`:@@alert.touring.allTriggersTitle:Your triggered alerts list`,
      text: $localize`:@@alert.touring.allTriggersText:Here you can find the list of ongoing and resolved alerts for the selected alerts. You can click on one to view more details.`,
    },
    mapStep: {
      url: "alerts/map",
      title: $localize`:@@alert.touring.mapTitle:Your triggered alerts on a map`,
      text: $localize`:@@alert.touring.mapText:On the map, you can view ongoing alerts from equipment associated with a location.`,
    },
    configStep: {
      url: "alerts/rule/-1/config",
      title: $localize`:@@alert.touring.configTitle:Your alert settings`,
      text: $localize`:@@alert.touring.configText:When a single alert is selected, you can access its settings and triggered alerts.`,
    },
  };

  readonly tourSteps$ = computed(() => {
    const tourSteps = [];

    Object.entries(this.touring).forEach(([step, data]) => {
      tourSteps.push(step + "@" + data.url);
    });

    return tourSteps;
  });

  getTourTitle(step) {
    const stepData = this.touring[step];

    return stepData ? stepData.title : "";
  }

  getTourText(step) {
    const stepData = this.touring[step];

    return stepData ? stepData.text : "";
  }

  // ---------------------------
  // --- page header
  // ---------------------------

  readonly headers$ = computed(() => {
    const headers = [];

    headers.push({
      id: "alerts",
      label: $localize`Alerts`,
      url: "/alerts",
    });

    return headers;
  });

  // ---------------------------
  // --- alerts
  // ---------------------------

  private localAlertId = 1;

  private readonly alertsLastFetch = signal<Date>(null);
  private alertsRefreshDelay: number = 300; // 5 mins
  private readonly alertsLoading = signal<boolean>(false);
  readonly alertsLoading$ = computed(() => {
    return this.alertsLastFetch() === null || this.alertsLoading() === true;
  });

  private readonly alerts = signal<Alert[]>([]);
  private tourAlerts: Alert[] = [];

  private readonly alertsById = signal<Record<number, Alert>>({});
  private readonly alertsDataById = signal<Record<number, AlertData>>({});
  private tourAlertsById: Record<number, Alert> = {};
  private readonly _alertsById$ = computed(() => {
    const tourMode = this.appStore.tourMode$();

    return tourMode ? this.tourAlertsById : this.alertsById();
  });
  readonly alertsById$ = computed(() => {
    const alertsById = this._alertsById$();
    const alertsDataById = this.alertsDataById();

    const result: Record<number, Alert> = {};

    Object.entries(alertsById).forEach(([alertId, alert]) => {
      const finalAlert = alert.clone();
      const data = alertsDataById[alert.id];
      finalAlert.setData(data);

      result[finalAlert.id] = finalAlert;
    });

    return result;
  });

  readonly alerts$ = computed(() => Object.values(this.alertsById$()));

  readonly alertsByDefinitionId$ = computed(() => {
    const alertsById = this.alertsById$();

    if (!alertsById || Object.keys(alertsById).length === 0) return {};

    const result: Record<number, Alert[]> = {};

    Object.values(alertsById).forEach((alert) => {
      if (typeof alert.alert_definition_id === "number") {
        if (!Array.isArray(result[alert.alert_definition_id])) {
          result[alert.alert_definition_id] = [];
        }
        result[alert.alert_definition_id].push(alert);
      }
    });

    return result;
  });

  readonly alertsByMeterId$ = computed(() => {
    const alertsById = this.alertsById$();

    if (!alertsById || Object.keys(alertsById).length === 0) return {};

    const result: Record<string, Alert[]> = {};

    Object.values(alertsById).forEach((alert) => {
      if (
        typeof alert.device_uuid === "string" &&
        alert.device_uuid.trim().length > 0 &&
        alert.deviceType === DeviceType.meter
      ) {
        if (!Array.isArray(result[alert.device_uuid])) {
          result[alert.device_uuid] = [];
        }

        result[alert.device_uuid].push(alert);
      }
    });

    return result;
  });

  readonly alertsByGatewayId$ = computed(() => {
    const alertsById = this.alertsById$();

    if (!alertsById || Object.keys(alertsById).length === 0) return {};

    const result: Record<string, Alert[]> = {};

    Object.values(alertsById).forEach((alert) => {
      if (
        typeof alert.device_uuid === "string" &&
        alert.device_uuid.trim().length > 0 &&
        alert.deviceType === DeviceType.gateway
      ) {
        if (!Array.isArray(result[alert.device_uuid])) {
          result[alert.device_uuid] = [];
        }

        result[alert.device_uuid].push(alert);
      }
    });

    return result;
  });

  fetchAlerts(forceRefresh: boolean = false) {
    try {
      if (this.alertsLoading()) return;

      if (
        forceRefresh ||
        this.alertsLastFetch() === null ||
        this.utilsService.calculateElapsedSeconds(this.alertsLastFetch()) >
          this.alertsRefreshDelay
      ) {
        this.alertsLoading.set(true);
        console.log(LOG_TAG + "fetchAlerts...");

        this.alertService.getAlertsForStore().subscribe({
          next: (alerts: Alert[]) => {
            this.alerts.set(alerts);
            this.alertsById.set(this._computeIndexedAlerts(alerts));
            if (this.screenMode !== AlertScreenMode.Loading) {
              this.alertsLoading.set(false);
            }

            this.alertsLastFetch.set(new Date());
          },
          error: (err) => {
            console.error(LOG_TAG + "failed to fetch alerts : ", err);
            this.error.set(err.message || "An error occurred.");
            this.alertsLoading.set(false);
          },
        });
      }
    } catch (error) {
      console.error(LOG_TAG + "ERROR ON FETCH ALERTS", error);
    }
  }

  // private readonly alertsByPlaceId = signal<Record<number, Alert>>({});

  // ---------------------------
  // --- alerts filtering
  // ---------------------------

  // ---------------------------
  // --- alert sorting
  // ---------------------------

  private alertsOrderByTimer: any;

  private readonly alertsOrderBy = signal<string>("actives-first");
  readonly alertsOrderBy$ = this.alertsOrderBy.asReadonly();

  getSortedAlerts(alerts: Alert[]): Alert[] {
    // const currentAlertsSort = this.currentAlertsSort$();
    let filteredAlerts = alerts.map((alert) => alert.clone());

    // first : sorted on more recent first
    const sortedAlerts = filteredAlerts.sort(
      (alertA, alertB) =>
        alertB.begin_date.toMillis() - alertA.begin_date.toMillis()
    );

    let result = [];
    // secondly : first active

    // get actives
    const activeAlerts = sortedAlerts.filter(
      (alert) => alert.is_active === true
    );
    result = [...activeAlerts];

    // get inactives
    const inactiveAlerts = sortedAlerts.filter(
      (alert) => alert.is_active === false
    );
    result = [...result, ...inactiveAlerts];

    // third : apply filter
    // let limit = null;
    // if (limit) {
    //   result = result.slice(0, limit);
    // }

    return result;
  }

  readonly alertsSortsById$ = computed(() => {
    const sorts: OrderByItem[] = [
      {
        id: "actives-first",
        field: "activeAlerts",
        direction: OrderByDirection.desc,
        label:
          $localize`:@@alert.sort.active:Sorted on alerts` +
          "\n" +
          $localize`:@@alert.sort.activeToInactive:(actives > inactives)`,
      },
      {
        id: "triggeredDate",
        field: "triggeredDate",
        direction: OrderByDirection.desc,
        label:
          $localize`:@@alert.sort.triggered:Sorted on triggered` +
          "\n" +
          $localize`:@@alert.sort.triggeredDesc:(newest > oldest)`,
      },
    ];

    const sortItems = {};
    sorts.map((sort) => {
      sortItems[sort.id] = sort;
    });

    return sortItems;
  });

  readonly alertsSorts$ = computed(() => {
    const alertsSortsById = this.alertsSortsById$();

    return Object.values(alertsSortsById);
  });

  readonly currentAlertsSort$ = computed(() => {
    const alertsSortsById = this.alertsSortsById$();
    const alertsOrderBy = this.alertsOrderBy$();

    if (!alertsOrderBy) return null;

    return alertsSortsById[alertsOrderBy];
  });

  updateAlertsOrderBy(newOrderBy: string): void {
    if (newOrderBy !== this.alertsOrderBy()) {
      if (this.alertsOrderByTimer) {
        clearTimeout(this.alertsOrderByTimer);
      }

      this.alertsOrderByTimer = setTimeout(() => {
        this.alertsOrderBy.set(newOrderBy);
      }, 100);
    }
  }

  // ---------------------------
  // --- alert definitions
  // ---------------------------

  private localAlertDefId = 1;

  private readonly definitionsLastFetch = signal<Date>(null);
  private definitionsRefreshDelay: number = 3600; // 1 hour
  private readonly definitionsLoading = signal<boolean>(false);
  private tourAlertsDefinitions: AlertDefinition[] = [];
  private readonly alertDefinitions = signal<AlertDefinition[]>([]);
  readonly definitionsLoading$ = computed(() => {
    return (
      this.definitionsLastFetch() === null || this.definitionsLoading() === true
    );
  });
  private readonly definitionsById = signal<Record<number, AlertDefinition>>(
    {}
  );
  private readonly definitionsDataById = signal<
    Record<number, AlertDefinitionData>
  >({});
  private tourDefinitionsById: Record<number, AlertDefinition> = {};
  private readonly _definitionsById$ = computed(() => {
    const tourMode = this.appStore.tourMode$();

    return tourMode ? this.tourDefinitionsById : this.definitionsById();
  });

  readonly definitionsById$ = computed(() => {
    const definitionsById = this._definitionsById$();
    const definitionsDataById = this.definitionsDataById();

    const result: Record<number, AlertDefinition> = {};

    Object.entries(definitionsById).forEach(([definitionId, definition]) => {
      const finalDef = definition.clone();
      const data = definitionsDataById[definition.id];
      if (data) {
        finalDef.setData(data);
      }

      result[definition.id] = finalDef;
    });

    return result;
  });

  fetchAlertDefinitions(forceRefresh: boolean = false) {
    try {
      if (this.definitionsLoading()) return;

      if (
        forceRefresh ||
        this.definitionsLastFetch() === null ||
        this.utilsService.calculateElapsedSeconds(this.definitionsLastFetch()) >
          this.definitionsRefreshDelay
      ) {
        this.definitionsLoading.set(true);
        console.log(LOG_TAG + "fetchAlertDefinitions...");

        this.alertDefinitionService.getAlertDefinitionsForStore().subscribe({
          next: (alertDefinitions: AlertDefinition[]) => {
            this.alertDefinitions.set(alertDefinitions);
            this.definitionsById.set(
              this._computeIndexedAlertsDefinitions(alertDefinitions)
            );
            if (this.screenMode !== AlertScreenMode.Loading) {
              this.definitionsLoading.set(false);
            }

            this.definitionsLastFetch.set(new Date());
          },
          error: (err) => {
            console.error(
              LOG_TAG + "failed to fetch alert definitions : ",
              err
            );
            this.error.set(err.message || "An error occurred.");
            this.definitionsLoading.set(false);
          },
        });
      }
    } catch (error) {
      console.error(LOG_TAG + "ERROR ON FETCH ALERTS", error);
    }
  }

  // ---------------------------
  // --- alert definition actions
  // ---------------------------

  private readonly adding = signal<boolean>(false);
  readonly adding$ = this.adding.asReadonly();
  private readonly addResult = signal<AlertDefinition | string | null>(null);
  readonly addResult$ = this.addResult.asReadonly();

  addDefinition(newDefinition: AlertDefinition) {
    if (this.adding()) return;

    this.addResult.set(null);
    this.adding.set(true);

    const tourMode = this.appStore.tourMode$();

    if (tourMode) {
      // prepare new showerhead
      newDefinition.id = this.localAlertDefId++;

      // update stores
      this.tourAlertsDefinitions.push(newDefinition);
      this.tourDefinitionsById[newDefinition.id] = newDefinition;

      this.adding.set(false);
      this.addResult.set(newDefinition);
    } else {
      this.alertDefinitionService
        .addAlertDefinitionForStore(newDefinition)
        .subscribe({
          next: (definition: AlertDefinition) => {
            this.adding.set(false);
            this.addResult.set(definition);
          },
          error: (err) => {
            console.error(LOG_TAG + "failed to add alert definition : ", err);
            this.addResult.set(err.error_code || "unknown");
            this.adding.set(false);
          },
        });
    }
  }

  declareAddSuccess() {
    const tourMode = this.appStore.tourMode$();
    if (!tourMode) {
      // refresh store
      this.fetchAlertDefinitions(true);
    }
    this.addResult.set(null);
  }

  resetAddResult() {
    this.addResult.set(null);
  }

  private readonly updating = signal<boolean>(false);
  readonly updating$ = this.updating.asReadonly();
  private readonly updateResult = signal<AlertDefinition | string | null>(null);
  readonly updateResult$ = this.updateResult.asReadonly();

  updateDefinition(updatedDefinition: AlertDefinition) {
    if (this.updating()) return;

    this.updateResult.set(null);
    this.updating.set(true);

    const tourMode = this.appStore.tourMode$();

    if (tourMode) {
      // // prepare new showerhead
      // newDefinition.id = this.localAlertDefId++;

      //TODO

      // update stores
      //TODO

      this.updating.set(false);
      this.updateResult.set(updatedDefinition);
    } else {
      this.alertDefinitionService
        .updateAlertDefinitionForStore(updatedDefinition)
        .subscribe({
          next: (definition: AlertDefinition) => {
            this.updating.set(false);
            this.updateResult.set(definition);
          },
          error: (err) => {
            console.error(
              LOG_TAG + "failed to update alert definition : ",
              err
            );
            this.updateResult.set(err.error_code || "unknown");
            this.updating.set(false);
          },
        });
    }
  }

  declareUpdateSuccess() {
    const tourMode = this.appStore.tourMode$();
    if (!tourMode) {
      // refresh store
      this.fetchAlertDefinitions(true);
    }
    this.updateResult.set(null);
  }

  resetUpdateResult() {
    this.updateResult.set(null);
  }

  private readonly batchUpdating = signal<boolean>(false);
  readonly batchUpdating$ = this.batchUpdating.asReadonly();
  private readonly batchUpdateResult = signal<boolean | string | null>(null);
  readonly batchUpdateResult$ = this.batchUpdateResult.asReadonly();

  batchUpdateDefinitions(alertDefIds: number[], props: any) {
    if (this.batchUpdating()) return;

    this.batchUpdateResult.set(null);
    this.batchUpdating.set(true);

    const tourMode = this.appStore.tourMode$();

    if (tourMode) {
      // // prepare new showerhead
      // newDefinition.id = this.localAlertDefId++;

      //TODO

      // update stores
      //TODO

      this.batchUpdating.set(false);
      this.batchUpdateResult.set(true);
    } else {
      this.alertDefinitionService
        .batchUpdateAlertDefinitionsForStore(alertDefIds, props)
        .subscribe({
          next: (result: boolean) => {
            this.batchUpdating.set(false);
            this.batchUpdateResult.set(true);
          },
          error: (err) => {
            console.error(
              LOG_TAG + "failed to batch update alert definition : ",
              err
            );
            this.batchUpdateResult.set(err.error_code || "unknown");
            this.batchUpdating.set(false);
          },
        });
    }
  }

  declareBatchUpdateSuccess() {
    const tourMode = this.appStore.tourMode$();
    if (!tourMode) {
      // refresh store
      this.fetchAlertDefinitions(true);
    }
    this.batchUpdateResult.set(null);
  }

  resetBatchUpdateResult() {
    this.batchUpdateResult.set(null);
  }

  private readonly deleting = signal<boolean>(false);
  readonly deleting$ = this.deleting.asReadonly();
  private readonly deleteResult = signal<boolean | string | null>(null);
  readonly deleteResult$ = this.deleteResult.asReadonly();

  deleteDefinition(definitionId: number) {
    if (this.deleting()) return;

    this.deleteResult.set(null);
    this.deleting.set(true);

    const tourMode = this.appStore.tourMode$();

    if (tourMode) {
      // // prepare new showerhead
      // newDefinition.id = this.localAlertDefId++;

      //TODO

      // update stores
      //TODO

      this.deleting.set(false);
      this.deleteResult.set(true);
    } else {
      this.alertDefinitionService
        .deletAlertDefinitionForStore(definitionId)
        .subscribe({
          next: (definition: AlertDefinition) => {
            this.deleting.set(false);
            this.deleteResult.set(true);
          },
          error: (err) => {
            console.error(
              LOG_TAG + "failed to update alert definition : ",
              err
            );
            this.deleteResult.set(err.error_code || "unknown");
            this.deleting.set(false);
          },
        });
    }
  }

  declareDeleteSuccess() {
    const tourMode = this.appStore.tourMode$();
    if (!tourMode) {
      // refresh store
      this.fetchAlertDefinitions(true);
    }
    this.deleteResult.set(null);
  }

  resetDeleteResult() {
    this.deleteResult.set(null);
  }

  // ---------------------------
  // --- alert rules
  // ---------------------------

  private readonly rulesById = signal<Record<number, AlertRule>>({});

  readonly rulesById$ = this.rulesById.asReadonly();

  readonly ruleIds$ = computed(() => {
    const rulesById = this.rulesById$();

    return Object.keys(rulesById).map((ruleId) => parseInt(ruleId));
  });

  readonly rules$ = computed(() => Object.values(this.rulesById$()));

  public isMeterCompatibleWithRule(rule: AlertRule, meter: Meter): boolean {
    switch (rule.type) {
      case RuleType.leak:
        if (METER_DRAC_TYPES.indexOf(meter.type) >= 0) {
          return meter.features.indexOf(MeterFeature.smart_alerts) >= 0;
        }
        return METER_3P_TYPES.indexOf(meter.type) >= 0;
      case RuleType.periodicity:
      case RuleType.smart:
        return (
          METER_DRAC_TYPES.indexOf(meter.type) >= 0 &&
          meter.features.indexOf(MeterFeature.smart_alerts) >= 0
        );
      case RuleType.duration:
      case RuleType.volume:
        return METER_DRAC_TYPES.indexOf(meter.type) >= 0;
      case RuleType.gateway_offline:
        return false;
      case RuleType.meter_offline:
      case RuleType.low_capacitor:
      case RuleType.daily_volume:
      case RuleType.monthly_volume:
        return true;
    }
  }

  // ---------------------------
  // --- alert rules sorting
  // ---------------------------

  private orderByTimer: any;

  private readonly orderBy = signal<string>("actives-first");
  readonly orderBy$ = this.orderBy.asReadonly();

  readonly sortsById$ = computed(() => {
    const sorts: OrderByItem[] = [
      {
        id: "actives-first",
        field: "activeAlerts",
        direction: OrderByDirection.desc,
        label:
          $localize`:@@alert.sort.active:Sorted on alerts` +
          "\n" +
          $localize`:@@alert.sort.activeToInactive:(actives > inactives)`,
      },
      {
        id: "label-asc",
        field: "label",
        direction: OrderByDirection.asc,
        label:
          $localize`:@@global.sort.label:Sorted on label` +
          "\n" +
          $localize`:@@global.sort.aToZ:(A > Z)`,
      },
      {
        id: "label-desc",
        field: "label",
        direction: OrderByDirection.desc,
        label:
          $localize`:@@global.sort.label:Sorted on label` +
          "\n" +
          $localize`:@@global.sort.zToA:(Z > A)`,
      },
      {
        id: "category-asc",
        field: "category",
        direction: OrderByDirection.asc,
        label: $localize`:@@alert.sort.type:Sorted on type` + "\n" + "(A > Z)",
      },
      {
        id: "category-desc",
        field: "category",
        direction: OrderByDirection.desc,
        label: $localize`:@@alert.sort.type:Sorted on type` + "\n" + "(Z > A)",
      },
    ];

    const sortItems = {};
    sorts.map((sort) => {
      sortItems[sort.id] = sort;
    });

    return sortItems;
  });

  readonly sorts$ = computed(() => {
    const sortsById = this.sortsById$();

    return Object.values(sortsById);
  });

  readonly currentSort$ = computed(() => {
    const sortsById = this.sortsById$();
    const orderBy = this.orderBy$();

    if (!orderBy) return null;

    return sortsById[orderBy];
  });

  updateOrderBy(newOrderBy: string): void {
    if (newOrderBy !== this.orderBy()) {
      if (this.orderByTimer) {
        clearTimeout(this.orderByTimer);
      }

      this.orderByTimer = setTimeout(() => {
        this.orderBy.set(newOrderBy);
      }, 100);
    }
  }

  // ---------------------------
  // --- filtering
  // ---------------------------

  private rulesSearchTimer: any;

  private readonly rulesSearch = signal<string>(null);
  readonly rulesSearch$ = this.rulesSearch.asReadonly();

  updateRuleSearch(newSearch: string): void {
    if (newSearch !== this.rulesSearch()) {
      if (this.rulesSearchTimer) {
        clearTimeout(this.rulesSearchTimer);
      }

      this.rulesSearchTimer = setTimeout(() => {
        this.rulesSearch.set(newSearch);
      }, 100);
    }
  }

  readonly filteredRules$ = computed(() => {
    const search = this.rulesSearch();
    let filteredRules = this.rules$();
    const currentSort = this.currentSort$();

    filteredRules = filteredRules.map((rule) => rule.clone());

    // filter on search
    const fixedSearch =
      typeof search === "string" && search.trim().length > 0
        ? search.trim().toLowerCase()
        : null;
    if (fixedSearch) {
      filteredRules = filteredRules.filter(
        (rule) =>
          rule.label?.trim().toLowerCase().indexOf(fixedSearch) >= 0 ||
          rule.description?.trim().toLowerCase().indexOf(fixedSearch) >= 0 ||
          rule.targetLabel?.trim().toLowerCase().indexOf(fixedSearch) >= 0 ||
          rule.categoryLabel?.trim().toLowerCase().indexOf(fixedSearch) >= 0
      );
      if (filteredRules.length === 0) return filteredRules;
    }

    // apply order by
    if (currentSort) {
      if (currentSort.field === "label") {
        if (currentSort.direction === OrderByDirection.asc) {
          filteredRules = filteredRules.sort((ruleA, ruleB) =>
            ruleA.label?.localeCompare(ruleB.label)
          );
        } else {
          filteredRules = filteredRules.sort((ruleA, ruleB) =>
            ruleB.label?.localeCompare(ruleA.label)
          );
        }
      } else if (currentSort.field === "activeAlerts") {
        // console.log("SORT BY LAST_SEEN", currentSort);
        if (currentSort.direction === OrderByDirection.desc) {
          filteredRules = filteredRules.sort(
            (ruleA, ruleB) =>
              ruleB.activeAlerts.length - ruleA.activeAlerts.length
          );
        }
      } else if (currentSort.field === "category") {
        if (currentSort.direction === OrderByDirection.asc) {
          filteredRules = filteredRules.sort((ruleA, ruleB) =>
            ruleA.category?.localeCompare(ruleB.category)
          );
        } else {
          filteredRules = filteredRules.sort((ruleA, ruleB) =>
            ruleB.category?.localeCompare(ruleA.category)
          );
        }
      }
    }

    return filteredRules;
  });

  readonly filteredAlerts$ = computed(() => {
    const currentAlertsSort = this.currentAlertsSort$();
    let filteredAlerts = this.alerts$();

    filteredAlerts = filteredAlerts.map((alert) => alert.clone());

    if (currentAlertsSort) {
      if (currentAlertsSort.field === "activeAlerts") {
        if (currentAlertsSort.direction === OrderByDirection.desc) {
          filteredAlerts = filteredAlerts.sort(
            (alertA, alertB) =>
              alertB.end_date.toMillis() - alertA.end_date.toMillis()
          );
        }
      } else if (currentAlertsSort.field === "triggeredDate") {
        if (currentAlertsSort.direction === OrderByDirection.desc) {
          filteredAlerts = filteredAlerts.sort(
            (alertA, alertB) =>
              alertB.begin_date.toMillis() - alertA.begin_date.toMillis()
          );
        }
      }
    }
  });

  // ---------------------------
  // --- selection
  // ---------------------------

  private rulesSelectedTimer: any;
  private ruleUrlTimer: any;

  private readonly rulesSelected = signal<number[]>([]);
  readonly rulesSelected$ = this.rulesSelected.asReadonly();

  private readonly unselectRecently = signal<boolean>(false);
  readonly unselectRecently$ = this.unselectRecently.asReadonly();

  readonly rule$ = computed(() => {
    const rulesById = this.rulesById$();
    const rulesSelected = this.rulesSelected();
    if (rulesSelected.length !== 1 || Object.keys(rulesById).length === 0)
      return null;

    return rulesById[rulesSelected[0]];
  });

  updateRulesSelected(ruleIds: number[]) {
    //TODO vérifier si showerhead_uuids a changé

    if (this.rulesSelectedTimer) {
      clearTimeout(this.rulesSelectedTimer);
    }

    this.rulesSelectedTimer = setTimeout(() => {
      this.rulesSelected.set(ruleIds);
    }, 100);
  }

  selectRule(ruleId: number) {
    if (this.rulesSelected().indexOf(ruleId) < 0) {
      const selected = [...this.rulesSelected()];
      selected.push(ruleId);
      this.rulesSelected.set(selected);
    }
  }

  selectNone() {
    if (this.rulesSelected().length > 0) {
      this.rulesSelected.set([]);
      this.unselectRecently.set(true);
    }
  }

  unselectRule(ruleIdToUnselect: number) {
    if (this.rulesSelected().indexOf(ruleIdToUnselect) >= 0) {
      const selected = this.rulesSelected().filter(
        (ruleId) => ruleId !== ruleIdToUnselect
      );
      this.unselectRecently.set(true);
      this.rulesSelected.set(selected);
    }
  }

  resetUnselectRecently() {
    if (this.unselectRecently() === true) {
      this.unselectRecently.set(false);
    }
  }

  async updateRuleToShowInUrl(ruleId: number): Promise<void> {
    if (this.ruleUrlTimer) {
      clearTimeout(this.ruleUrlTimer);
    }

    this.ruleUrlTimer = setTimeout(async () => {
      await this.router.navigate(
        [ruleId ? "/alerts/rule/" + ruleId : "/alerts"],
        {
          replaceUrl: true,
          queryParamsHandling: "merge",
        }
      );
    }, 400);
  }

  // ---------------------------
  // --- navigation
  // ---------------------------

  // Getter status signals for external access
  readonly loading$ = computed(() => {
    const alertsLoading = this.alertsLoading$();
    const definitionsLoading = this.definitionsLoading$();
    const definitionsById = this._definitionsById$();

    if (definitionsLoading === false) {
      return Object.keys(definitionsById).length > 0 ? alertsLoading : false;
    } else {
      return definitionsLoading;
    }
  });

  triggerForcedRefresh(): void {
    this.forceRefresh.set(true);
  }

  // ---------------------------
  // --- LIFE CYCLE METHODS
  // ---------------------------

  public constructor() {
    this._prepareTourData();

    effect(
      () => {
        const forcedRefresh = this.forceRefresh();
        if (forcedRefresh === true) {
          console.log(LOG_TAG + "EFFECT / force refresh definitions & alerts");
          this.fetchAlertDefinitions(true);
          this.fetchAlerts(true);
          this.forceRefresh.set(false);
        }
      },
      { allowSignalWrites: true }
    );

    effect(
      () => {
        const alertsById = this._alertsById$();
        const user = this.userStore.user$();
        const metersById = this.meterStore.metersById$();
        const gatewaysById = this.gatewayStore.gatewaysById$();
        const alertsDataById: Record<number, AlertData> = {};

        if (!user || Object.keys(alertsById).length === 0) {
          this.alertsDataById.set(alertsDataById);
          return;
        }

        console.log(
          LOG_TAG +
            "EFFECT / prepare alertData from alerts, meters, gateways and meters"
        );

        Object.entries(alertsById).forEach(([alertId, alert]) => {
          const data: AlertData = {
            deviceLabel: null,
            devicePlaceLabel: null,
            detailsLabel: null,
            label: null,
          };

          if (alert.deviceType === DeviceType.meter) {
            const meter = metersById[alert.device_uuid];
            if (meter) {
              data.deviceLabel = meter.universalLabel;
              data.devicePlaceLabel = this.placesStore.getLabel(meter.place_id);
            }
          } else if (alert.deviceType === DeviceType.gateway) {
            const gateway = gatewaysById[alert.device_uuid];
            if (gateway) {
              data.deviceLabel = gateway.device;
            }
          }

          data.label = alert.getLabel(user, this.utilsService);
          data.detailsLabel = alert.getDetailsLabel(user, this.utilsService);

          alertsDataById[alert.id] = data;
        });

        this.alertsDataById.set(alertsDataById);
      },
      { allowSignalWrites: true }
    );

    effect(
      () => {
        const definitionsById = this.definitionsById$();
        const alertsByDefinitionId = this.alertsByDefinitionId$();
        const alertRulesById: Record<number, AlertRule> = {};

        this.unselectRecently.set(false);

        if (Object.keys(definitionsById).length === 0) {
          this.rulesById.set(alertRulesById);
          return;
        }

        console.log(
          LOG_TAG + "EFFECT / build alertRules from definitions and alerts"
        );

        Object.entries(definitionsById).forEach(
          ([definitionId, definition]) => {
            // alertRules from definitions & alerts
            const alerts = alertsByDefinitionId[definition.id];
            const ruleId = definition.ruleId;
            let rule = alertRulesById[ruleId];
            if (!rule) {
              // create rule here
              rule = new AlertRule(
                ruleId,
                definition.ruleType,
                definition.ruleTargetMeter,
                definition.ruleTargetPlace
              );
            }

            rule.definitions.push(definition);
            if (Array.isArray(alerts) && alerts.length > 0) {
              rule.alerts = [...rule.alerts, ...alerts];
            }

            alertRulesById[ruleId] = rule;
          }
        );

        this.rulesById.set(alertRulesById);
      },
      { allowSignalWrites: true }
    );

    effect(
      () => {
        const definitionsById = this._definitionsById$();
        const user = this.userStore.user$();
        const metersById = this.meterStore.metersById$();
        const placesById = this.placesStore.placesById$();
        const definitionsDataById: Record<number, AlertDefinitionData> = {};

        if (!user) return;

        if (Object.keys(definitionsById).length === 0) {
          this.definitionsDataById.set(definitionsDataById);
          return;
        }

        console.log(
          LOG_TAG +
            "EFFECT / refresh definitions targetLabel and ruleLabel from user, meters and places"
        );

        Object.entries(definitionsById).forEach(
          ([definitionId, definition]) => {
            const data: AlertDefinitionData = {
              targetLabel: null,
              ruleLabel: null,
            };

            data.ruleLabel = definition.getRuleLabel(user, this.utilsService);
            if (
              definition.target_meter !== null &&
              metersById[definition.target_meter]
            ) {
              data.targetLabel =
                metersById[definition.target_meter].universalLabel;
            } else if (
              definition.target_place !== null &&
              placesById[definition.target_place]
            ) {
              data.targetLabel = this.placesStore.getLabel(
                definition.target_place
              );
            }

            definitionsDataById[definition.id] = data;
          }
        );

        this.definitionsDataById.set(definitionsDataById);
      },
      { allowSignalWrites: true }
    );
  }

  public clear() {
    // reset local values

    // reset debounces
    clearTimeout(this.rulesSelectedTimer);
    clearTimeout(this.orderByTimer);
    clearTimeout(this.ruleUrlTimer);

    // reset states
    this.localAlertId = 1;
    this.alertsLastFetch.set(null);
    this.alertsLoading.set(false);
    this.alerts.set([]);
    this.alertsById.set({});
    this.alertsDataById.set({});

    this.alertsOrderBy.set("actives-first");

    this.localAlertDefId = 1;
    this.definitionsLastFetch.set(null);
    this.definitionsLoading.set(false);
    this.alertDefinitions.set([]);
    this.definitionsById.set({});
    this.definitionsDataById.set({});

    this.adding.set(false);
    this.addResult.set(null);

    this.updating.set(false);
    this.updateResult.set(null);

    this.batchUpdating.set(false);
    this.batchUpdateResult.set(null);

    this.deleting.set(false);
    this.deleteResult.set(null);

    this.rulesById.set({});

    this.orderBy.set("actives-first");

    this.rulesSearch.set(null);

    this.rulesSelected.set([]);
    this.unselectRecently.set(false);

    this.forceRefresh.set(false);
    this.error.set(null);
  }

  public _computeIndexedAlerts(alerts: Alert[]): Record<number, Alert> {
    const index: Record<number, Alert> = {};

    alerts.forEach((alert) => {
      index[alert.id] = alert;
    });

    return index;
  }

  public _computeIndexedAlertsDefinitions(
    alerts: AlertDefinition[]
  ): Record<number, AlertDefinition> {
    const index: Record<number, AlertDefinition> = {};

    alerts.forEach((alertDef) => {
      index[alertDef.id] = alertDef;
    });

    return index;
  }

  // ---------------------------
  // --- PRIVATE METHODS
  // ---------------------------

  // ---------------------------
  // --- TOUR METHODS
  // ---------------------------

  private _prepareTourData() {
    switch (this.appStore.alertsScenario) {
      case AlertTourScenario.None:
        // nothing to do
        break;
      case AlertTourScenario.One:
        this._prepareAlertsDef(this.appStore.alertsScenario);
        break;
      case AlertTourScenario.Full:
        this._prepareAlertsDef(this.appStore.alertsScenario);
        break;
      case AlertTourScenario.Test:
        this._prepareAlertsDef(this.appStore.alertsScenario);
        break;
    }

    // console.log("alertsForDemo", this.tourAlerts);

    // prepare alerts
    this.tourDefinitionsById = this._computeIndexedAlertsDefinitions(
      this.tourAlertsDefinitions
    );
    this.tourAlertsById = this._computeIndexedAlerts(this.tourAlerts);
  }

  private _prepareManualTest(): void {
    const volumeDef = this._createDefinition(
      this.localAlertDefId,
      AlertType.volume,
      "Swimming pool leak",
      "8336312405000023",
      null,
      true
    );
    const volumeAlert = this._addAlertFromDefinition(
      this.localAlertId,
      AlertType.volume,
      Math.random() < 0.5, //random true or false,
      "8336312405000023",
      this.localAlertDefId
    );
    this.tourAlerts.push(volumeAlert);

    this.localAlertId++;
    this.localAlertDefId++;

    const durationDef = this._createDefinition(
      this.localAlertDefId,
      AlertType.duration,
      null,
      null,
      4,
      true
    );
    const durationAlert = this._addAlertFromDefinition(
      this.localAlertId,
      AlertType.duration,
      Math.random() < 0.5, //random true or false,
      "8336312405000020",
      this.localAlertDefId
    );
    this.tourAlerts.push(durationAlert);

    this.localAlertId++;
    this.localAlertDefId++;

    const dailyVolumeDef = this._createDefinition(
      this.localAlertDefId,
      AlertType.daily_volume,
      null,
      null,
      null,
      true
    );
    const dailyVolAlert = this._addAlertFromDefinition(
      this.localAlertId,
      AlertType.daily_volume,
      Math.random() < 0.5, //random true or false,
      "8336312405000012",
      this.localAlertDefId
    );
    this.tourAlerts.push(dailyVolAlert);

    this.localAlertId++;
    this.localAlertDefId++;

    const monthlyVolumeDef = this._createDefinition(
      this.localAlertDefId,
      AlertType.monthly_volume,
      null,
      null,
      5,
      true
    );
    const monthlyVolAlert = this._addAlertFromDefinition(
      this.localAlertId,
      AlertType.monthly_volume,
      Math.random() < 0.5, //random true or false,
      "8336312405000021",
      this.localAlertDefId
    );
    this.tourAlerts.push(monthlyVolAlert);

    this.localAlertId++;
    this.localAlertDefId++;

    this.tourAlertsDefinitions.push(volumeDef);
    this.tourAlertsDefinitions.push(durationDef);
    this.tourAlertsDefinitions.push(dailyVolumeDef);
    this.tourAlertsDefinitions.push(monthlyVolumeDef);
  }

  private _createDefinition(
    id: number,
    type: AlertType,
    label: string,
    target_meter: string,
    target_place: number,
    enabled: boolean
  ): AlertDefinition {
    const created_at = DateTime.local().minus({
      month: Math.random() * 10 + 1,
    });
    const updated_at = DateTime.local().minus({ days: Math.random() * 30 });
    const value = Math.floor(
      minValue[type] + Math.random() * (maxValue[type] - minValue[type])
    );

    return new AlertDefinition(
      id,
      type,
      label,
      target_meter,
      target_place,
      enabled,
      value,
      Math.random() < 0.5, //random true or false
      minValue[type],
      maxValue[type],
      created_at,
      updated_at
    );
  }

  private _addAlertFromDefinition(
    id: number,
    type: AlertType,
    is_active: boolean,
    device_uuid: string,
    alert_definition_id: number
  ): Alert {
    const begin_date = DateTime.local().minus({
      hour: Math.max(Math.random() * 17520, 15),
    });
    const end_date = is_active
      ? DateTime.local()
      : begin_date.plus({ hour: Math.random() * 14 });
    const notified_on = begin_date; //REWORK ?
    const end_notified_on = end_date; //REWORK ?
    const distance = Math.floor(Math.random() * maxDistance[type]);
    const flow = Math.floor(0.5 + Math.random() * 50);

    return new Alert(
      id,
      type,
      is_active,
      begin_date,
      end_date,
      notified_on,
      end_notified_on,
      distance,
      flow,
      device_uuid,
      alert_definition_id
    );
  }

  private _prepareAlertsDef(scenario: AlertTourScenario): void {
    // generate all automatic def
    const meters = Object.values(this.meterStore.tourMetersById$());
    meters.forEach((meter) => {
      this._createDefForMeterAdded(meter);
    });
    if (meters.length > 0) {
      this._createMeterOfflineDef();
      this._createLowCapacitorDef();
      this._createGatewayOfflineDef();

      if (scenario == AlertTourScenario.Test) {
        // generate all manual def
        this._prepareManualTest();
      }
    }
  }

  private _createMeterOfflineDef(): void {
    const alertDef = this._createDefinition(
      this.localAlertDefId,
      AlertType.meter_offline,
      null,
      null,
      null,
      true
    );
    this.tourAlertsDefinitions.push(alertDef);

    // generate alerts
    const amountAlert = 2 + Math.floor(Math.random() * 10);
    for (let i = 0; i < amountAlert; i++) {
      // we need an ID bewteen 10 and 34 to match meters uuid
      const deviceUuid =
        "83363124050000" + String(10 + Math.floor(Math.random() * 24));
      const alert = this._addAlertFromDefinition(
        this.localAlertId,
        AlertType.meter_offline,
        Math.random() < 0.5, //random true or false,
        deviceUuid,
        this.localAlertDefId
      );
      this.tourAlerts.push(alert);

      this.localAlertId++;
    }

    this.localAlertDefId++;
  }

  private _createLowCapacitorDef(): void {
    const alertDef = this._createDefinition(
      this.localAlertDefId,
      AlertType.low_capacitor,
      null,
      null,
      null,
      true
    );
    this.tourAlertsDefinitions.push(alertDef);

    // generate alerts
    const amountAlert = 2 + Math.floor(Math.random() * 10);
    for (let i = 0; i < amountAlert; i++) {
      // we need an ID bewteen 10 and 34 to match meters uuid
      const deviceUuid =
        "83363124050000" + String(10 + Math.floor(Math.random() * 24));
      const alert = this._addAlertFromDefinition(
        this.localAlertId,
        AlertType.low_capacitor,
        Math.random() < 0.5, //random true or false,
        deviceUuid,
        this.localAlertDefId
      );
      this.tourAlerts.push(alert);

      this.localAlertId++;
    }

    this.localAlertDefId++;
  }

  private _createGatewayOfflineDef(): void {
    const alertDef = this._createDefinition(
      this.localAlertDefId,
      AlertType.gateway_offline,
      null,
      null,
      null,
      true
    );
    this.tourAlertsDefinitions.push(alertDef);

    // generate alerts
    const amountAlert = 2 + Math.floor(Math.random() * 10);
    for (let i = 0; i < amountAlert; i++) {
      // we need an ID bewteen 1 and 4 to match LoRa gateways uuid
      const deviceUuid =
        "00005813d3093eb" + String(1 + Math.floor(Math.random() * 4));
      const alert = this._addAlertFromDefinition(
        this.localAlertId,
        AlertType.gateway_offline,
        false,
        deviceUuid,
        this.localAlertDefId
      );
      this.tourAlerts.push(alert);

      this.localAlertId++;
    }

    const activeAlert1 = this._addAlertFromDefinition(
      this.localAlertId,
      AlertType.gateway_offline,
      true,
      "00005813d3093eb3",
      this.localAlertDefId
    );
    this.tourAlerts.push(activeAlert1);

    this.localAlertId++;

    const activeAlert2 = this._addAlertFromDefinition(
      this.localAlertId,
      AlertType.gateway_offline,
      true,
      "00005813d3093eb2",
      this.localAlertDefId
    );
    this.tourAlerts.push(activeAlert2);

    this.localAlertId++;

    this.localAlertDefId++;
  }

  private _createDefForMeterAdded(meter: Meter): void {
    for (const systemAlertDefId in SYSTEM_ALERT_DEFINITIONS) {
      const systemAlertDef = SYSTEM_ALERT_DEFINITIONS[systemAlertDefId];
      if (
        systemAlertDef.meterTypes.indexOf(meter.type) >= 0 &&
        (systemAlertDef.feature === null ||
          meter.features.indexOf(systemAlertDef.feature) >= 0)
      ) {
        const alertDefinition = this._createDefinition(
          this.localAlertDefId,
          systemAlertDef.alertType,
          null,
          meter.device_uuid,
          null,
          Math.random() < 0.5 //random true or false
        );
        this.tourAlertsDefinitions.push(alertDefinition);

        this.localAlertDefId++;

        // generate alerts
        if (alertDefinition.enabled) {
          const amountAlert = Math.floor(Math.random() * 5);
          for (let i = 0; i < amountAlert; i++) {
            const alert = this._addAlertFromDefinition(
              this.localAlertId,
              systemAlertDef.alertType,
              Math.random() < 0.5, //random true or false,
              meter.device_uuid,
              alertDefinition.id
            );
            this.tourAlerts.push(alert);

            this.localAlertId++;
          }
        }
      }
    }
  }
}
